import { useAutocomplete } from 'admin';
import { getInternalMaterialProductName, SiteMaterialList } from 'api';

import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';

type Props = Omit<AutocompleteFieldProps<SiteMaterialList>, 'options'> & {
  tradeCompanyId?: number;
  tradeSite?: number;
  defaultValue?: SiteMaterialList['id'];
};

const getOptionLabel = (option: SiteMaterialList) => {
  if (option.material_product) {
    return getInternalMaterialProductName(option.material_product);
  }

  // We shouldn't reach this point because we filter the options to have a material product
  // but we need to return a string for the defaultValue because we don't have the data
  // at initial render
  return 'No material product';
};

export const SiteMaterialWithProductAutocomplete = ({
  tradeCompanyId,
  tradeSite,
  required = false,
  defaultValue,
  ...rest
}: Props) => {
  const { autocompleteProps } = useAutocomplete<SiteMaterialList>({
    defaultValue,
    filters: [{ field: 'site', operator: 'eq', value: tradeSite }],
    queryOptions: {
      enabled: Boolean(tradeCompanyId),
    },
    resource: `/trade/v1/trade-companies/${tradeCompanyId}/site-materials/`,
  });
  return (
    <AutocompleteField<SiteMaterialList>
      {...autocompleteProps}
      getOptionLabel={getOptionLabel}
      groupBy={(option) => option.site.full_address}
      filterOptions={(options, _) => options.filter((option) => option.material_product)}
      data-testid="site-material-selector"
      disabled={!tradeCompanyId}
      defaultValue={defaultValue}
      valueField="id"
      required={required}
      {...rest}
    />
  );
};
